import { Component, OnInit, OnDestroy } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { AppState } from "src/app/shared/state/app.state";
import { CompanySettingsVM, Setting } from "../../shared/models/setting.model";
import { SettingsService } from "../../shared/services/settings.service";
import { forkJoin, Subscription } from "rxjs";
import { UIState } from "../../shared/state/ui.state";
import { AuthService } from "../../auth/auth.service";
import { OnDeactivate, OnDeactivatePayload } from "../../shared/guards/can-deactivate.guard";
import { environment } from "src/environments/environment";
import { MessageService } from "../../shared/services/message.service";
import { OperationType } from "src/app/shared/models/user.model";
import { Router } from "@angular/router";
import { ExportFinancialServiceService } from "src/app/shared/services/export-financial-service.service";
import { take, tap } from "rxjs/operators";
import { ExportDownloadType } from "src/app/shared/models/export-download-type.model";

@Component({
  selector: "app-company-details",
  templateUrl: "./company-details.component.html",
  styleUrls: ["./company-details.component.scss"],
})
export class CompanyDetailsComponent implements OnInit, OnDeactivate, OnDestroy {
  environment = environment;

  private subscriptions: Subscription[] = [];

  saving: boolean;
  loading: boolean = false;

  settings: CompanySettingsVM;

  form: FormGroup = this.fb.group({
    Niscode: [null],
    Name: [null],
    ServiceCell: [null],
    Street: [null],
    HouseNumber: [null],
    PostalCode: [null],
    City: [null],
    Telephone: [null],
    Fax: [null],
    Email: [null],
    HasInventory: [false],

    ExportFdExcludeGem: [false],
    ExportFdResetAge: [false],
    ExportFdExportType: ["legacy"],

    CadastralSurfaceEnabled: [false],
    TotalSurfaceEnabled: [false],
    BuildingLayersEnabled: [false],
    FloorSurfaceEnabled: [false],
    SideLengthEnabled: [false],
    TotalStreetsOnSideEnabled: [false],
    LocationTypeEnabled: [false],
    DistrictEnabled: [false],
    OOIsAccordion: [false],

    AutofillCity: [false],
  });

  exportFdDownloadTypes: ExportDownloadType[] = [];

  constructor(
    public appState: AppState,
    public authService: AuthService,
    public uiState: UIState,
    private fb: FormBuilder,
    private settingsService: SettingsService,
    private messageService: MessageService,
    private router: Router,
    private exportFinancialService: ExportFinancialServiceService,
  ) {}

  ngOnInit(): void {
    this.appState.allowedToChangeCommunity = false;

    if (!this.appState.activeCommunity$.value?.IsCommunity) {
      this.router.navigateByUrl("/");
    }

    this.uiState.breadcrumbs$.next([
      {
        name: "Algemene instellingen",
      },
    ]);

    this.subscriptions.push(
      this.authService.hasScreenResource("BEDRIJFSGEGEVENS", OperationType.UPDATE).subscribe((hasPermissions) => {
        if (!hasPermissions) {
          this.form.disable();
        }

        this.getSettings();
      }),
    );
  }

  onDeactivate(): OnDeactivatePayload {
    return {
      hasChanges: this.form.dirty,
    };
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  save(): void {
    if (this.saving) {
      return;
    }

    if (this.form.invalid) {
      this.messageService.showSnackBarError("Gelieve alle verplichte velden in te vullen.");
      return;
    }

    this.saving = true;

    const settings = { ...this.settings, ...this.form.getRawValue() };
    this.subscriptions.push(
      this.settingsService.updateCompanySettings(settings).subscribe(
        (settings: Setting[]) => {
          this.saving = false;

          this.getSettings();
          this.form.markAsPristine();
        },
        (error: any) => {
          this.saving = false;
        },
      ),
    );
  }

  getSettings(): void {
    this.loading = true;
    this.subscriptions.push(
      forkJoin({
        settings: this.settingsService.getCompanySettings(),
        downloadTypes: this.exportFinancialService.getDownloadTypes(),
      })
        .pipe(
          take(1),
          tap(({ settings, downloadTypes }) => {
            this.exportFdDownloadTypes = downloadTypes;
            this.settings = settings;
            this.form.patchValue(settings);

            this.loading = false;
          }),
        )
        .subscribe(),
    );
  }
}
